import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { useUserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import { useGlobalHotkey } from '~utils/hooks/use-global-hotkey'
import {
    canCallerUseAskChckvet,
    isAskChckvetEnabledForClinic,
} from './ask-chckvet-enabled'

/**
 * Ask Chckvet dock state, shared by the header trigger, universal search, and
 * the dock itself.
 *
 * The dock is a persistent work surface (Cursor-style), not a modal: open
 * state and width survive navigation and reloads so staff can keep a thread
 * alongside the page they're working on.
 */

const OPEN_STORAGE_KEY = 'chckup.askChckvet.dock.open.v1'
const WIDTH_STORAGE_KEY = 'chckup.askChckvet.dock.width.v1'
const MODE_STORAGE_KEY = 'chckup.askChckvet.dock.mode.v1'

export const ASK_CHCKVET_MIN_WIDTH = 340
export const ASK_CHCKVET_MAX_WIDTH = 760
export const ASK_CHCKVET_DEFAULT_WIDTH = 440

/**
 * Width the dock currently occupies at the right edge, published so
 * viewport-anchored UI (e.g. the floating chat popovers) can shift out from
 * under it without importing this context.
 */
export const ASK_CHCKVET_WIDTH_CSS_VAR = '--ask-chckvet-dock-width'

/**
 * `dock` splits the shell and narrows the page; `overlay` floats above it.
 * Overlay is the safe mode on pages that already have their own right-hand
 * panel (availability checker, enterprise reports), where two side panels
 * leave the main content unusably narrow.
 */
export type AskChckvetDisplayMode = 'dock' | 'overlay'

export type OpenAskChckvetOptions = {
    /** Prefill the composer, e.g. from universal search. Consumed once. */
    seedPrompt?: string
    /** Focus an existing thread instead of whatever was last open. */
    conversationId?: string
    /** Start a fresh thread. */
    newChat?: boolean
    /** Send `seedPrompt` immediately instead of leaving it in the composer. */
    autoSend?: boolean
}

export type AskChckvetDockContextValue = {
    /** Clinic eligible for Ask Chckvet + any clinic staff role. */
    isEnabled: boolean
    isOpen: boolean
    open: (options?: OpenAskChckvetOptions) => void
    close: () => void
    toggle: () => void
    activeConversationId: string | null
    setActiveConversationId: (conversationId: string | null) => void
    startNewChat: () => void
    /** Pending composer prefill; call `consumeSeedPrompt` after applying it. */
    seedPrompt: string | null
    /** When true, the thread should send `seedPrompt` instead of only prefilling. */
    autoSendSeed: boolean
    consumeSeedPrompt: () => void
    width: number
    setWidth: (width: number) => void
    displayMode: AskChckvetDisplayMode
    setDisplayMode: (mode: AskChckvetDisplayMode) => void
}

const AskChckvetDockContext = createContext<AskChckvetDockContextValue | null>(
    null,
)

function readStoredOpen(): boolean {
    if (typeof window === 'undefined') return false
    try {
        return window.localStorage.getItem(OPEN_STORAGE_KEY) === 'true'
    } catch {
        return false
    }
}

function readStoredMode(): AskChckvetDisplayMode {
    if (typeof window === 'undefined') return 'dock'
    try {
        return window.localStorage.getItem(MODE_STORAGE_KEY) === 'overlay'
            ? 'overlay'
            : 'dock'
    } catch {
        return 'dock'
    }
}

function readStoredWidth(): number {
    if (typeof window === 'undefined') return ASK_CHCKVET_DEFAULT_WIDTH
    try {
        const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY)
        const parsed = raw ? Number(raw) : NaN
        if (!Number.isFinite(parsed)) return ASK_CHCKVET_DEFAULT_WIDTH
        return clampWidth(parsed)
    } catch {
        return ASK_CHCKVET_DEFAULT_WIDTH
    }
}

export function clampWidth(width: number): number {
    return Math.min(
        ASK_CHCKVET_MAX_WIDTH,
        Math.max(ASK_CHCKVET_MIN_WIDTH, Math.round(width)),
    )
}

function writeStored(key: string, value: string): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(key, value)
    } catch {
        // Private mode / quota — dock still works for this session.
    }
}

export function AskChckvetDockProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const { clinic, role } = useClinic()
    const { isAdmin } = useUserContext()
    const clinicId = clinic?.id

    const [isOpen, setIsOpen] = useState(false)
    const [activeConversationId, setActiveConversationId] = useState<
        string | null
    >(null)
    const [seedPrompt, setSeedPrompt] = useState<string | null>(null)
    const [autoSendSeed, setAutoSendSeed] = useState(false)
    const [width, setWidthState] = useState(ASK_CHCKVET_DEFAULT_WIDTH)
    const [displayMode, setDisplayModeState] =
        useState<AskChckvetDisplayMode>('dock')

    // Read persisted dock state after mount so SSR markup stays stable.
    useEffect(() => {
        setIsOpen(readStoredOpen())
        setWidthState(readStoredWidth())
        setDisplayModeState(readStoredMode())
    }, [])

    const isEnabled =
        canCallerUseAskChckvet(role, isAdmin) &&
        isAskChckvetEnabledForClinic(clinic)

    // Threads are clinic-scoped; switching clinics must not leave another
    // clinic's conversation focused.
    useEffect(() => {
        setActiveConversationId(null)
    }, [clinicId])

    const setOpenPersisted = useCallback((next: boolean) => {
        setIsOpen(next)
        writeStored(OPEN_STORAGE_KEY, String(next))
    }, [])

    const open = useCallback(
        (options?: OpenAskChckvetOptions) => {
            if (options?.newChat) {
                setActiveConversationId(null)
            } else if (options?.conversationId) {
                setActiveConversationId(options.conversationId)
            }
            if (options?.seedPrompt) {
                setSeedPrompt(options.seedPrompt)
                setAutoSendSeed(Boolean(options.autoSend))
            }
            setOpenPersisted(true)
        },
        [setOpenPersisted],
    )

    const close = useCallback(() => setOpenPersisted(false), [setOpenPersisted])

    const toggle = useCallback(
        () => setOpenPersisted(!isOpen),
        [isOpen, setOpenPersisted],
    )

    const startNewChat = useCallback(() => {
        setActiveConversationId(null)
        setSeedPrompt(null)
        setAutoSendSeed(false)
    }, [])

    const consumeSeedPrompt = useCallback(() => {
        setSeedPrompt(null)
        setAutoSendSeed(false)
    }, [])

    const setWidth = useCallback((next: number) => {
        const clamped = clampWidth(next)
        setWidthState(clamped)
        writeStored(WIDTH_STORAGE_KEY, String(clamped))
    }, [])

    const setDisplayMode = useCallback((mode: AskChckvetDisplayMode) => {
        setDisplayModeState(mode)
        writeStored(MODE_STORAGE_KEY, mode)
    }, [])

    const isVisible = isOpen && isEnabled

    // Publish the occupied right-edge width so viewport-fixed UI (chat
    // popovers) can offset itself. Both modes sit against the right edge, so
    // the offset applies whether we're splitting the shell or floating.
    useEffect(() => {
        if (typeof document === 'undefined') return undefined
        const root = document.documentElement
        root.style.setProperty(
            ASK_CHCKVET_WIDTH_CSS_VAR,
            isVisible ? `${width}px` : '0px',
        )
        return () => {
            root.style.removeProperty(ASK_CHCKVET_WIDTH_CSS_VAR)
        }
    }, [isVisible, width])

    // Cmd/Ctrl+I mirrors the "summon the agent" shortcut people expect.
    useGlobalHotkey({
        key: 'i',
        modifier: 'mod',
        enabled: isEnabled,
        onKeyDown: toggle,
    })

    const value = useMemo<AskChckvetDockContextValue>(
        () => ({
            isEnabled,
            isOpen: isVisible,
            open,
            close,
            toggle,
            activeConversationId,
            setActiveConversationId,
            startNewChat,
            seedPrompt,
            autoSendSeed,
            consumeSeedPrompt,
            width,
            setWidth,
            displayMode,
            setDisplayMode,
        }),
        [
            isEnabled,
            isVisible,
            open,
            close,
            toggle,
            activeConversationId,
            startNewChat,
            seedPrompt,
            autoSendSeed,
            consumeSeedPrompt,
            width,
            setWidth,
            displayMode,
            setDisplayMode,
        ],
    )

    return (
        <AskChckvetDockContext.Provider value={value}>
            {children}
        </AskChckvetDockContext.Provider>
    )
}

export function useAskChckvetDock(): AskChckvetDockContextValue {
    const context = useContext(AskChckvetDockContext)
    if (!context) {
        throw new Error(
            'useAskChckvetDock must be used inside an AskChckvetDockProvider.',
        )
    }
    return context
}
